import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Lock, Delete, AlertCircle, RefreshCw, Unlock as UnlockIcon,
  ChevronRight, Clock, Plus, CheckCircle2
} from 'lucide-react';

const MIN_PIN = 4;
const MAX_PIN = 8;
const SESSION_KEY = 'gl_guest_session';

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function saveSession(session) {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch (_) { /* ignore */ }
}

function formatRemaining(ms) {
  if (ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * iOS-style slide-to-unlock. The guest must drag the thumb to the end of the
 * track to fire onUnlock - a deliberate gesture, so the momentary latch only
 * releases when they are standing at the locker.
 */
const SwipeToUnlock = ({ onUnlock, disabled, busy }) => {
  const trackRef = useRef(null);
  const [drag, setDrag] = useState(0); // 0..1
  const dragging = useRef(false);
  const startX = useRef(0);

  const THUMB = 64; // px, matches h-16/w-16

  const positionFromEvent = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const max = track.offsetWidth - THUMB - 8; // 8 = track padding
    const delta = clientX - startX.current;
    return Math.min(1, Math.max(0, delta / max));
  }, []);

  const onPointerDown = (e) => {
    if (disabled || busy) return;
    dragging.current = true;
    startX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setDrag(positionFromEvent(e.clientX));
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setDrag((d) => {
      if (d > 0.88) {
        onUnlock();
        return 1;
      }
      return 0;
    });
  };

  // Snap back when an attempt finishes.
  useEffect(() => {
    if (!busy) setDrag(0);
  }, [busy]);

  const track = trackRef.current;
  const maxOffset = track ? track.offsetWidth - THUMB - 8 : 0;

  return (
    <div
      ref={trackRef}
      className={`relative h-[72px] rounded-full material-regular border border-white/10 select-none overflow-hidden ${disabled ? 'opacity-50' : ''}`}
    >
      {/* progress fill */}
      <div
        className="absolute inset-y-0 left-0 bg-primary-500/25 rounded-full transition-none"
        style={{ width: `${drag * 100}%` }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span
          className="text-body text-secondary tracking-wide"
          style={{ opacity: 1 - drag * 1.6 }}
        >
          {busy ? 'Opening...' : 'Slide to unlock'}
        </span>
      </div>
      <div
        className="absolute top-1 left-1 h-16 w-16 rounded-full bg-gradient-to-b from-primary-400 to-primary-600 shadow-glow flex items-center justify-center touch-none cursor-grab active:cursor-grabbing"
        style={{
          transform: `translateX(${drag * maxOffset}px)`,
          transition: dragging.current ? 'none' : 'transform 0.25s ease'
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {busy
          ? <RefreshCw className="w-7 h-7 text-white animate-spin" />
          : <ChevronRight className="w-8 h-8 text-white" />}
      </div>
    </div>
  );
};

/**
 * Public guest unlock page (reached via the printed room QR / Tailscale
 * Funnel).
 *
 * Two modes:
 *  - Keypad: PIN only - the PIN resolves the locker server-side. A correct
 *    PIN NEVER opens the locker; it links this browser (one phone per
 *    locker) to the assignment.
 *  - Linked: the locker is pinned to this phone - countdown, +1h extensions,
 *    and a slide-to-unlock control that performs the actual open.
 */
const Unlock = () => {
  // 'checking' | 'keypad' | 'linked' | 'expired'
  const [mode, setMode] = useState('checking');
  const [session, setSession] = useState(null); // { token, locker_name, expires_at, extensions_left }
  const [remaining, setRemaining] = useState(0);

  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [shake, setShake] = useState(false);

  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState(false);
  const [extending, setExtending] = useState(false);

  // ----- boot: restore a stored session, if any -----
  useEffect(() => {
    const stored = loadSession();
    if (!stored || !stored.token) {
      setMode('keypad');
      return;
    }
    (async () => {
      try {
        const res = await axios.post('/api/public/session/status', { token: stored.token });
        const data = res.data || {};
        if (data.state === 'active') {
          const next = { ...stored, ...data, token: stored.token };
          setSession(next);
          saveSession(next);
          setMode('linked');
        } else {
          saveSession(null);
          setSession(null);
          setMode('expired');
        }
      } catch (_) {
        // Network hiccup: keep the token, fall back to keypad for now.
        setMode('keypad');
      }
    })();
  }, []);

  // ----- countdown -----
  useEffect(() => {
    if (mode !== 'linked' || !session?.expires_at) return undefined;
    const tick = () => {
      const ms = new Date(session.expires_at).getTime() - Date.now();
      setRemaining(ms);
      if (ms <= 0) {
        saveSession(null);
        setSession(null);
        setMode('expired');
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [mode, session]);

  // Clear transient failure styling
  useEffect(() => {
    if (!shake) return undefined;
    const t = setTimeout(() => setShake(false), 600);
    return () => clearTimeout(t);
  }, [shake]);

  // Auto-hide the "locker open" confirmation
  useEffect(() => {
    if (!opened) return undefined;
    const t = setTimeout(() => setOpened(false), 5000);
    return () => clearTimeout(t);
  }, [opened]);

  const fail = (message) => {
    setError(message);
    setShake(true);
  };

  // ----- keypad: verify PIN -> link this phone -----
  const submit = async () => {
    if (loading) return;
    if (pin.length < MIN_PIN) {
      fail(`Your PIN has at least ${MIN_PIN} digits.`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/api/public/unlock', { pin });
      const s = res.data?.session;
      if (s && s.token) {
        saveSession(s);
        setSession(s);
        setPin('');
        setError(null);
        setNotice('Phone linked - this locker is now yours to open below.');
        setMode('linked');
      } else {
        fail('Could not link your phone. Please try again.');
        setPin('');
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not link your phone. Please try again.';
      fail(msg);
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // ----- linked: swipe -> open -----
  const openLocker = async () => {
    if (!session?.token || opening) return;
    setOpening(true);
    setError(null);
    try {
      await axios.post('/api/public/session/open', { token: session.token });
      setOpened(true);
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.state === 'expired' || data.state === 'not-found') {
        saveSession(null);
        setSession(null);
        setMode('expired');
      } else {
        setError(data.error || 'Could not open the locker. Please try again.');
      }
    } finally {
      setOpening(false);
    }
  };

  const extend = async () => {
    if (!session?.token || extending) return;
    setExtending(true);
    setError(null);
    try {
      const res = await axios.post('/api/public/session/extend', { token: session.token });
      const next = {
        ...session,
        expires_at: res.data.expires_at,
        extensions_left: res.data.extensions_left
      };
      setSession(next);
      saveSession(next);
      setNotice('Added 1 hour to your locker.');
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.state === 'expired' || data.state === 'not-found') {
        saveSession(null);
        setSession(null);
        setMode('expired');
      } else {
        setError(data.error || 'Could not extend right now.');
      }
    } finally {
      setExtending(false);
    }
  };

  const clearLink = () => {
    saveSession(null);
    setSession(null);
    setNotice(null);
    setError(null);
    setMode('keypad');
  };

  const pressKey = (digit) => {
    if (loading) return;
    setPin((p) => (p.length >= MAX_PIN ? p : p + digit));
  };

  const backspace = () => {
    if (loading) return;
    setPin((p) => p.slice(0, -1));
  };

  // ===== checking =====
  if (mode === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-secondary animate-spin" />
      </div>
    );
  }

  // ===== expired =====
  if (mode === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center animate-fade-up">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-warning-500/15 border border-warning-500/30 flex items-center justify-center">
            <Clock className="w-10 h-10 text-warning-400" />
          </div>
          <h1 className="text-title1 text-white mb-3">Session ended</h1>
          <p className="text-secondary text-body mb-8">
            Your locker session has ended. Please visit the INZAN front desk to
            get a locker.
          </p>
          <button onClick={clearLink} className="btn btn-secondary w-full py-3.5">
            I have a new PIN
          </button>
        </div>
      </div>
    );
  }

  // ===== linked =====
  if (mode === 'linked' && session) {
    return (
      <div className="min-h-screen flex items-center justify-center p-5">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="text-center mb-6">
            <p className="text-footnote text-label uppercase tracking-widest mb-2">Your locker</p>
            <div className="text-[72px] leading-none font-semibold text-white tracking-tight mb-3">
              {session.locker_name}
            </div>
            <div className="inline-flex items-center space-x-2 material-thin rounded-full px-4 py-1.5">
              <Clock className="w-4 h-4 text-secondary" />
              <span className={`text-subheadline tabular-nums ${remaining < 5 * 60 * 1000 ? 'text-warning-400' : 'text-secondary'}`}>
                {formatRemaining(remaining)} left
              </span>
            </div>
          </div>

          <div className="glass-sheet p-5">
            {opened ? (
              <div className="text-center py-4 animate-pop">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-success-500/15 border border-success-500/30 flex items-center justify-center shadow-glow-green">
                  <CheckCircle2 className="w-8 h-8 text-success-400" />
                </div>
                <p className="text-title3 text-white mb-1">Locker open</p>
                <p className="text-footnote text-muted">
                  The latch releases for a few seconds - open the door now.
                </p>
              </div>
            ) : (
              <>
                <SwipeToUnlock onUnlock={openLocker} busy={opening} disabled={false} />
                <p className="text-caption text-muted text-center mt-3">
                  Stand at your locker before sliding - it unlocks for a moment.
                </p>
              </>
            )}
          </div>

          {session.extensions_left > 0 && (
            <button
              onClick={extend}
              disabled={extending}
              className="btn btn-tinted w-full py-3.5 mt-4"
            >
              {extending
                ? <><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Extending...</>
                : <><Plus className="w-5 h-5 mr-2" /> Add 1 hour ({session.extensions_left} left)</>}
            </button>
          )}

          {notice && (
            <div className="mt-4 material-regular rounded-2xl p-3.5 flex items-start space-x-3 animate-fade-up">
              <CheckCircle2 className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              <p className="text-subheadline text-secondary">{notice}</p>
            </div>
          )}
          {error && (
            <div className="mt-4 material-regular rounded-2xl p-3.5 flex items-start space-x-3 border-danger-500/30 animate-fade-up">
              <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              <p className="text-subheadline text-danger-300">{error}</p>
            </div>
          )}

          <button
            onClick={clearLink}
            className="block mx-auto mt-6 text-footnote text-muted underline underline-offset-2"
          >
            Not your locker? Unlink this phone
          </button>
        </div>
      </div>
    );
  }

  // ===== keypad =====
  const dotCount = Math.max(MIN_PIN, pin.length);

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-b from-primary-400 to-primary-600 flex items-center justify-center shadow-glow">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-title1 text-white">Enter your PIN</h1>
          <p className="text-secondary text-subheadline mt-1">
            The PIN from the front desk. Your phone becomes the key.
          </p>
        </div>

        <div className={`glass-sheet p-6 ${shake ? 'animate-shake' : ''}`}>
          <div className="flex items-center justify-center space-x-4 mb-6 min-h-[14px]">
            {Array.from({ length: dotCount }).map((_, i) => (
              <span key={i} className={`pin-dot ${i < pin.length ? 'filled' : ''}`} />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 justify-items-center mb-5">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button key={d} type="button" className="pinpad-key" onClick={() => pressKey(d)}>
                {d}
              </button>
            ))}
            <span />
            <button type="button" className="pinpad-key" onClick={() => pressKey('0')}>
              0
            </button>
            <button
              type="button"
              className="pinpad-key !text-[20px]"
              onClick={backspace}
              aria-label="Delete digit"
            >
              <Delete className="w-6 h-6" />
            </button>
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={loading || pin.length < MIN_PIN}
            className="btn btn-primary w-full py-4 text-body"
          >
            {loading ? (
              <><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Linking...</>
            ) : (
              <><UnlockIcon className="w-5 h-5 mr-2" /> Link this phone</>
            )}
          </button>

          <p className="text-caption text-muted text-center mt-4">
            Linking does not open the locker - you will swipe to unlock on the
            next screen.
          </p>
        </div>

        {error && (
          <div className="mt-4 material-regular rounded-2xl p-4 flex items-start space-x-3 border-danger-500/30 animate-fade-up">
            <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
            <p className="text-subheadline text-danger-300">{error}</p>
          </div>
        )}

        <p className="text-center text-caption text-muted mt-6">
          Locked out or lost your PIN? Visit the front desk.
        </p>
      </div>
    </div>
  );
};

export default Unlock;
